import { motion } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { fadeInUp, staggerContainer } from "@/lib/motion";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/layout/PageHeader";
import { useLanguage } from "@/components/LanguageContext";
import { SEO } from "@/components/layout/SEO";
import { Code, DatabaseScript, Globe, LaptopDevMode, DataTransferBoth } from "iconoir-react";

export default function Software() {
  const { t } = useLanguage();

  const products = [
    {
      icon: Code,
      title: "Traffic Counter AI",
      desc: t("Déployez des modèles de vision par ordinateur pour compter, classifier et suivre les véhicules en temps réel. Générez automatiquement des matrices origine-destination.", "Deploy edge or cloud-based computer vision models to count, classify, and track vehicles in real-time. Automatically generate Origin-Destination matrices and detailed turning movement counts."),
      features: [t("Plus de 8 Classes de Véhicules", "8+ Vehicle Classes"), t("Suivi Directionnel", "Directional Tracking"), t("Synchronisation Multi-Caméras", "Multi-Camera Sync"), t("Intégration API", "API Integration")],
      status: t("Prêt pour la Production", "Production Ready")
    },
    {
      icon: Globe,
      title: "ZFE Simulator",
      desc: t("Un jumeau numérique complet pour modéliser les Zones à Faibles Émissions. Simulez les déviations de trafic, calculez l'impact des émissions (NOx, PM) et évaluez les effets.", "A comprehensive digital twin for modeling Low-Emission Zones. Simulate traffic diversion, calculate emissions impact (NOx, PM), and evaluate socioeconomic effects of different perimeters."),
      features: [t("Modélisation des Émissions", "Emissions Modeling"), t("Affectation du Trafic", "Traffic Assignment"), t("Visualisation SIG", "GIS Visualization"), t("Comparaison de Scénarios", "Scenario Comparison")],
      status: t("Enterprise Uniquement", "Enterprise Only")
    },
    {
      icon: DatabaseScript,
      title: "Territorial Intelligence Platform",
      desc: t("Un tableau de bord SIG centralisé qui agrège des données de mobilité multi-sources, des couches démographiques et les conditions des infrastructures sur une seule carte.", "A centralized GIS dashboard that aggregates multi-source mobility data, demographic layers, and infrastructure conditions into a single operational map."),
      features: [t("Intégration Open Data", "Open Data Integration"), t("Génération de Cartes de Chaleur", "Heatmap Generation"), t("Rapports Personnalisés", "Custom Reporting"), t("Tableaux de Bord Interactifs", "Interactive Dashboards")],
      status: t("Déploiement Sur Mesure", "Custom Deployment")
    }
  ];

  return (
    <div className="bg-charcoal text-warm-white min-h-screen">
      <SEO 
        title={t("Logiciels & Applications", "Software & Apps")}
        description={t(
          "Découvrez nos solutions numériques : Traffic Counter AI, ZFE Simulator et notre plateforme d'intelligence territoriale.",
          "Explore our digital solutions: Traffic Counter AI, ZFE Simulator, and our territorial intelligence platform."
        )}
      />
      <PageHeader 
        overlineFr="Logiciels & Applications"
        overlineEn="Software & Apps"
        titleFr="Le Cœur Numérique de l'Intelligence Territoriale."
        titleEn="The Digital Core of Territorial Intelligence."
        descriptionFr="Nous concevons des logiciels analytiques et des jumeaux numériques pour transformer vos données de mobilité complexes en atouts opérationnels."
        descriptionEn="We build proprietary analytics software and digital twins to transform complex spatial and mobility data into operational assets."
        animatedGif={true}
      />

      <section className="py-20 lg:py-32 relative z-10">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <motion.div 
              variants={fadeInUp} 
              initial="hidden" 
              whileInView="visible" 
              viewport={{ once: true }} 
              className="lg:col-span-4 space-y-8"
            >
               <div>
                  <div className="w-12 h-12 rounded-xl bg-amber/10 text-amber flex items-center justify-center mb-4">
                    <LaptopDevMode width={24} height={24} />
                  </div>
                  <h3 className="text-xl font-display font-semibold mb-2">{t("Conçu pour les Planificateurs", "Built for Planners")}</h3>
                  <p className="text-warm-white/50 text-sm leading-relaxed">
                    {t("Contrairement aux outils SaaS génériques, nos logiciels sont spécialement conçus pour les planificateurs de la mobilité, les ingénieurs de trafic et les agences publiques.", "Unlike generic SaaS tools, our software is engineered specifically for mobility planners, traffic engineers, and public agencies.")}
                  </p>
               </div>
               <div>
                  <div className="w-12 h-12 rounded-xl bg-blue-grey/10 text-blue-grey flex items-center justify-center mb-4">
                    <DataTransferBoth width={24} height={24} />
                  </div>
                  <h3 className="text-xl font-display font-semibold mb-2">{t("Intégration de Données", "Data Integration")}</h3>
                  <p className="text-warm-white/50 text-sm leading-relaxed">
                    {t("Connectez facilement vos capteurs de trafic existants, les données API TomTom/Google, et vos bases de données SIG locales.", "Seamlessly connect your existing traffic sensors, TomTom/Google API data, and local GIS repositories.")}
                  </p>
               </div>
            </motion.div>

            <motion.div 
              variants={staggerContainer} 
              initial="hidden" 
              whileInView="visible" 
              viewport={{ once: true }} 
              className="lg:col-span-8 space-y-6"
            >
               {products.map((prod, i) => (
                 <motion.div 
                   key={i} 
                   variants={fadeInUp}
                   className="p-8 rounded-2xl bg-warm-white/[0.03] border border-warm-white/10 hover:border-amber/30 transition-colors duration-500"
                 >
                    <div className="flex flex-col sm:flex-row justify-between items-start mb-6 gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber flex items-center justify-center text-charcoal">
                           <prod.icon width={24} height={24} strokeWidth={2} />
                        </div>
                        <h2 className="text-2xl font-display font-semibold">{prod.title}</h2>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest px-3 py-1 bg-warm-white/10 text-warm-white border border-warm-white/20 rounded-full flex-shrink-0">
                        {prod.status}
                      </span>
                    </div>
                    <p className="text-warm-white/60 leading-relaxed mb-8">{prod.desc}</p>
                    <div className="grid grid-cols-2 gap-3 mb-8">
                       {prod.features.map((feat, j) => (
                         <div key={j} className="flex items-center gap-2 text-sm text-warm-white/40">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber" />
                            {feat}
                         </div>
                       ))}
                    </div>
                    <Button href="/contact?inquiry=Software Demo" variant="secondary" className="border-warm-white/20 text-warm-white hover:text-amber-dark hover:border-amber-dark">
                      {t("Demander une Démo", "Request Demo")}
                    </Button>
                 </motion.div>
               ))}
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="py-20 bg-warm-white text-charcoal rounded-t-[3rem] shadow-[0_-10px_40px_rgba(0,0,0,0.1)] relative z-20">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-semibold tracking-widest text-amber uppercase mb-2 block">
              {t("Outils Standards de l'Industrie", "Industry Standard Tools")}
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold">
              {t("Un Écosystème Connecté", "A Connected Ecosystem")}
            </h2>
            <p className="mt-4 text-graphite">
              {t(
                "Nous intégrons les meilleurs outils mondiaux d'ingénierie et de science des données pour fournir des solutions sur mesure.",
                "We integrate world-class engineering and data science tools to deliver tailor-made solutions."
              )}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mt-16">
            {[
              { name: "PTV Vissim", src: "/images/logos/PTVVissim.png" },
              { name: "PTV Visum", src: "/images/logos/Logo PTV VISUM.png" },
              { name: "AutoCAD", src: "/images/logos/AutoCad_new_logo.svg.png" },
              { name: "Autodesk Revit", src: "/images/logos/autodesk Revit.png" },
              { name: "Covadis", src: "/images/logos/logo-covadis.webp" },
              { name: "ArcGIS Pro", src: "/images/logos/Logo ArcGIS Pro.png" },
              { name: "TransCAD", src: "/images/logos/Logo TransCAD.svg" },
              { name: "Global Mapper", src: "/images/logos/Logo Global Mapper.png" },
              { name: "Pix4D", src: "/images/logos/Logo Pix4D.svg" },
              { name: "Power BI", src: "/images/logos/Logo Power BI.jpg" }
            ].map((logo, idx) => (
              <div 
                key={idx} 
                className="bg-white border border-mineral shadow-sm rounded-xl h-28 flex items-center justify-center p-6 hover:shadow-md hover:-translate-y-1 hover:border-amber/30 transition-all duration-500 group relative"
              >
                <div className="relative w-full h-full grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500 mix-blend-multiply">
                  <img src={logo.src} alt={logo.name} className="w-full h-full object-contain" />
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>
    </div>
  );
}
